import React, { useState, useEffect } from "react";
import { ArrowLeft, Plus, MapPin } from "lucide-react";
import OrderSummary from "../OrderSummery/OrderSummary";
import AddressForm from "./AddressForm";
import AddressCard from "./AddressCard";
import PaymentPage from "../Payment/PaymentPage";
import DeliveryService from "../../services/deliveryService";
import { useAuth } from "../../context/AuthContext";
import LocationPicker from "./LocationPicker";
import {
  getMyAddresses,
  addAddress,
  deleteAddress,
} from "../../utils/addressApi";
import "./addresspage.css";

const AddressPage = ({
  totalItems,
  totalAmount,
  deliveryCharge,
  getTaxBreakdown,
  discount,
  finalTotal,
  getDiscountAmount,
  calculateFinalTotal,
  cartItems,
  // ============ COUPON PROPS ============
  coupons = [],
  selectedCoupon = "",
  setSelectedCoupon = () => {},
  applyCoupon = () => {},
  couponMessage = "",
  couponLoading = false,
   removeCoupon = () => {}, 
   
}) => {
  const { accessToken } = useAuth();

  // Address state
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Payment state
  const [showPaymentPage, setShowPaymentPage] = useState(false);

  // Delivery state
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [recalculatingDelivery, setRecalculatingDelivery] = useState(false);


  const [showLocationPicker, setShowLocationPicker] = useState(false);
  // Address form state
  const [addressForm, setAddressForm] = useState({
    fullName: "",
    contactNumber: "",
    email: "",
    address: "",
    pincode: "",
    state: "",
    landmark: "",
    type: "Home",
  });

  // ============ FETCH ADDRESSES ============
  useEffect(() => {
    if (accessToken) fetchUserAddresses();
  }, [accessToken]);

  const fetchUserAddresses = async () => {
    try {
      setIsLoading(true);
      const response = await getMyAddresses(accessToken);
      console.log("Fetched addresses response:", response);

      const addressList = response.addresses || [];

      setAddresses(addressList);
      if (addressList.length > 0) setSelectedAddress(addressList[0]);
    } catch (error) {
      console.error("❌ Error fetching addresses:", error);
      alert("Failed to load addresses. Please try again.");
      setAddresses([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ============ RECALCULATE DELIVERY ============
  useEffect(() => {
    if (selectedAddress) {
      recalculateDeliveryForAddress(selectedAddress);
    }
  }, [selectedAddress, accessToken]);

  const recalculateDeliveryForAddress = async (address) => {
    if (!address?.latitude || !address?.longitude) {
      console.warn("⚠️ Address missing coordinates:", address._id);
      setDeliveryInfo(null);
      return;
    }

    try {
      setRecalculatingDelivery(true);

      const delivery = await DeliveryService.getDeliveryInfoFromAddress(
        address.latitude,
        address.longitude,
        accessToken
      );

      setDeliveryInfo(delivery);
    } catch (error) {
      console.error("❌ Delivery recalculation error:", error.message);
      setDeliveryInfo(null);
    } finally {
      setRecalculatingDelivery(false);
    }
  };

  // ============ GEOCODE ADDRESS ============
  const geocodeAddress = async (addressString) => {
    try {
      const coords = await DeliveryService.geocodeAddress(addressString);
      return coords;
    } catch (error) {
      console.error("❌ Geocoding error:", error.message);
      return null;
    }
  };

  // ============ ADDRESS FORM HANDLERS ============
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    try {
      let coordinatesToSave = { ...addressForm };

      // Try to geocode if no coordinates provided
      if (!addressForm.latitude || !addressForm.longitude) {
        const fullAddress = `${addressForm.address}, ${
          addressForm.landmark || ""
        }, ${addressForm.pincode}, ${addressForm.state}`;
        const coords = await geocodeAddress(fullAddress);

        if (coords) {
          coordinatesToSave = { ...addressForm, ...coords };
        } else {
          console.warn("⚠️ Could not geocode address, saving without coordinates");
        }
      }

      const newAddress = await addAddress(coordinatesToSave, accessToken);

      if (editingAddress) {
        setAddresses((prev) =>
          prev.map((addr) =>
            addr._id === editingAddress._id ? newAddress : addr
          )
        );
      } else {
        setAddresses((prev) => [...prev, newAddress]);
      }

      const message = editingAddress ? "Address updated!" : "Address saved!";
      alert(message);

      setShowAddressForm(false);
      setEditingAddress(null);
      resetAddressForm();

      if (!editingAddress && addresses.length === 0) {
        setSelectedAddress(newAddress);
      }
    } catch (error) {
      console.error("❌ Error saving address:", error);
      alert(error.response?.data?.message || "Failed to save address. Please try again.");
    }
  };

  const handleSelectAddress = (address) => {
    setSelectedAddress(address);
  };

  const handleEditAddress = (address) => {
    setAddressForm(address);
    setEditingAddress(address);
    setShowAddressForm(true);
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm("Are you sure you want to delete this address?"))
      return;
    try {
      await deleteAddress(addressId, accessToken);
      setAddresses((prev) => prev.filter((addr) => addr._id !== addressId));
      if (selectedAddress && selectedAddress._id === addressId) {
        setSelectedAddress(addresses.length > 1 ? addresses[0] : null);
      }
      alert("Address deleted!");
    } catch (error) {
      console.error("❌ Error deleting address:", error);
      alert("Failed to delete address. Please try again.");
    }
  };
const resetAddressForm = () => {
  setAddressForm({
    fullName: "",
    contactNumber: "",
    email: "",
    address: "",
    pincode: "",
    state: "",
    landmark: "",
    type: "Home",
    latitude: "",   // 🔥 IMPORTANT
    longitude: "",  // 🔥 IMPORTANT
  });
  };

  const handleAddNewAddress = () => {
    resetAddressForm();
    setEditingAddress(null);
    setShowAddressForm(true);
  };

  // ============ PAYMENT HANDLERS ============
  const handleProceedToPayment = () => {
    if (!selectedAddress) {
      alert("Please select a delivery address.");
      return;
    }
    console.log("Proceeding to payment with address:", selectedAddress);
    setShowPaymentPage(true);
  };

  const handleBackFromPayment = () => {
    setShowPaymentPage(false);
  };

  const handlePaymentSuccess = (paymentData) => {
    console.log("Payment successful:", paymentData);
    // Redirect to orders page or show success message
    // window.location.href = '/orders';
  };

  const handlePaymentFailure = (error) => {
    console.error("Payment failed:", error);
    alert(
      `Payment failed: ${error.description || "Please try again"}`
    );
  };

  // ============ DELIVERY INFO COMPONENT ============
  const DeliveryInfoBox = ({ deliveryInfo, isRecalculating }) => {
    if (!deliveryInfo) {
      return (
        <div className="delivery-info-box">
          {/* <p>Loading delivery information...</p> */}
        </div>
      );
    }

    const {
      distance,
      deliveryCharge,
      isFreeDelivery,
      estimatedDeliveryTime,
      message,
    } = deliveryInfo;

    return (
      <div className="delivery-info-box">
        <h3>Delivery Details</h3>

        {isRecalculating ? (
          <p className="delivery-loading">Calculating...</p>
        ) : (
          <>
            <p>
              <strong>Distance:</strong> {distance} km
            </p>
            <p>
              <strong>Delivery Charge:</strong>{" "}
              {isFreeDelivery ? (
                <span className="free-delivery">FREE</span>
              ) : (
                `₹${deliveryCharge}`
              )}
            </p>
            <p>
              <strong>Estimated Time:</strong> {estimatedDeliveryTime}
            </p>
            {message && <p className="delivery-message">{message}</p>}
          </>
        )}
      </div>
    );
  };

  // ============ LOADING STATE ============
  if (isLoading) {
    return (
      <div className="address-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your addresses...</p>
        </div>
      </div>
    );
  }

  // ============ PAYMENT PAGE ============
  if (showPaymentPage) {
    return (
    <PaymentPage
      selectedAddress={selectedAddress}
      totalItems={totalItems}
      totalAmount={totalAmount}
      deliveryCharge={deliveryInfo?.deliveryCharge || deliveryCharge}
      getTaxBreakdown={getTaxBreakdown}
      getDiscountAmount={getDiscountAmount} // 👈 discount
      calculateFinalTotal={calculateFinalTotal} // 👈 final total with coupon
      selectedCoupon={selectedCoupon} // 👈 added
      cartItems={cartItems}
      onBackToAddress={handleBackFromPayment}
      onPaymentSuccess={handlePaymentSuccess}
      onPaymentFailure={handlePaymentFailure}
    />
    );
  }

  // ============ RENDER ============
  return (

    
    <div className="address-page">
      <div className="address-container">
        {/* Header */}
        <div className="address-header">
          <h1 className="page-title">Select Delivery Address</h1>
          <p className="page-subtitle">
            Please select your delivery address to calculate accurate delivery
            charges
          </p>
        </div>

        <div className="address-main-layout">
          {/* Left Section - Address List */}
          <div className="address-section">
            {showAddressForm ? (
              <AddressForm
                addressForm={addressForm}
                handleAddressChange={handleAddressChange}
                handleAddressSubmit={handleAddressSubmit}
                handleBackToCart={() => {
                  setShowAddressForm(false);
                  setEditingAddress(null);
                  resetAddressForm();
                }}
                isEditing={!!editingAddress}
                onOpenLocationPicker={() => setShowLocationPicker(true)}
              />
            ) : (
              <div className="address-list-container">
                {addresses.length === 0 ? (
                  <div className="empty-address-state">
                    <div className="empty-address-icon">
                      <MapPin size={64} />
                    </div>
                    <h2 className="empty-address-title">No Saved Addresses</h2>
                    <p className="empty-address-description">
                      You haven't saved any addresses yet. Add your first
                      address to get started.
                    </p>
                    <button
                      className="primary-button add-first-address-btn"
                      onClick={handleAddNewAddress}
                    >
                      <Plus size={20} />
                      Add Your First Address
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="address-list-header">
                      <h2>Your Addresses</h2>
                      <p>Select a delivery address or add a new one</p>
                    </div>

                    <div className="addresses-grid">
                      {addresses.map((address) => (
                        <AddressCard
                          key={address._id}
                          address={address}
                          isSelected={
                            selectedAddress &&
                            selectedAddress._id === address._id
                          }
                          onSelect={handleSelectAddress}
                          onEdit={handleEditAddress}
                          onDelete={handleDeleteAddress}
                        />
                      ))}

                      <div
                        className="add-new-address-card"
                        onClick={handleAddNewAddress}
                      >
                        <div className="add-new-icon">
                          <Plus size={32} />
                        </div>
                        <h3>Add New Address</h3>
                        <p>Create a new delivery address</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
{showLocationPicker && (
  <LocationPicker
    isOpen={showLocationPicker}
    onClose={() => setShowLocationPicker(false)}
    onLocationSelect={(coords) => {
      setAddressForm((prev) => ({
        ...prev,
        latitude: coords.lat,
        longitude: coords.lng,
      }));
      setShowLocationPicker(false);
    }}
    initialLocation={
      addressForm.latitude && addressForm.longitude
        ? { lat: Number(addressForm.latitude), lng: Number(addressForm.longitude) }
        : null
    }
  />
)}
          {/* Right Section - Delivery Info + Order Summary */}
          <div className="order-summary-section">
            {selectedAddress && (
              <div className="selected-address-delivery-info">
                <DeliveryInfoBox
                  deliveryInfo={deliveryInfo}
                  isRecalculating={recalculatingDelivery}
                />
              </div>
            )}

            {/* Order Summary - UPDATED WITH COUPON PROPS */}
            <OrderSummary
              totalItems={totalItems}
              totalAmount={totalAmount}
              totalTax={0}
              deliveryCharge={deliveryInfo?.deliveryCharge || deliveryCharge}
              deliveryLoading={recalculatingDelivery}
              deliveryError={null}
              getTaxBreakdown={getTaxBreakdown}
              getDiscountAmount={getDiscountAmount || (() => discount)}
              calculateFinalTotal={calculateFinalTotal || (() => finalTotal)}
              // ============ COUPON PROPS ============
              coupons={coupons}
              selectedCoupon={selectedCoupon}
              setSelectedCoupon={setSelectedCoupon}
              applyCoupon={applyCoupon}
              couponMessage={couponMessage}
              couponLoading={couponLoading}
               onRemoveCoupon={removeCoupon}
              // ============ PAYMENT PROPS ============
              onProceedToPayment={handleProceedToPayment}
              buttonText="Proceed to Payment"
              showActions={!!selectedAddress}
              showTrustBadges={true}
              className="order-summary-address-page"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressPage;